import React, { useState, useEffect } from 'react';
import { useSubscriptionContext } from '@/contexts/SubscriptionContext';
import { formatDistanceToNow } from 'date-fns';
import { useLocation } from 'react-router-dom';
import { Clock, Sparkles, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { TrackingLink } from '@/lib/marketing';
import { useTrialBannerContext } from '@/contexts/TrialBannerContext';
import { useBarPackageAccess } from '@/hooks/useBarPackageAccess';

interface TrialBannerProps {
  className?: string;
  compact?: boolean;
}

/**
 * Banner component that shows trial status and countdown
 * Displays when user is in trial period
 */
export function TrialBanner({ className, compact = false }: TrialBannerProps) {
  const { subscription, isLoading } = useSubscriptionContext();
  
  // Don't show if loading or no subscription data
  if (isLoading || !subscription) {
    return null;
  }
  
  // Only show for trial subscriptions
  if (subscription.status !== 'trialing' || !subscription.current_period_end) {
    return null;
  }
  
  const trialEndsAt = new Date(subscription.current_period_end);
  const now = new Date();
  
  // Don't show if trial has expired
  if (trialEndsAt <= now) {
    return null;
  }
  
  const timeRemaining = formatDistanceToNow(trialEndsAt, { addSuffix: false });
  const daysRemaining = Math.ceil((trialEndsAt.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
  
  // Determine urgency for styling
  const isUrgent = daysRemaining <= 3;
  const isWarning = daysRemaining <= 7 && daysRemaining > 3;
  
  if (compact) {
    return (
      <div className={cn(
        "flex items-center gap-2 px-3 py-1.5 text-sm rounded-full",
        isUrgent && "bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400",
        isWarning && "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400",
        !isUrgent && !isWarning && "bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400",
        className
      )}>
        <Clock className="h-3.5 w-3.5" />
        <span className="font-medium">Trial: {daysRemaining}d left</span>
        <TrackingLink
          href="/pricing"
          campaign="trial_banner"
          content="compact_upgrade"
          className="underline hover:no-underline font-semibold"
        >
          Upgrade
        </TrackingLink>
      </div>
    );
  }
  
  return (
    <div className={cn(
      "w-full rounded-xl border p-4",
      isUrgent && "bg-red-50 border-red-200 dark:bg-red-900/10 dark:border-red-800",
      isWarning && "bg-yellow-50 border-yellow-200 dark:bg-yellow-900/10 dark:border-yellow-800",
      !isUrgent && !isWarning && "bg-blue-50 border-blue-200 dark:bg-blue-900/10 dark:border-blue-800",
      className
    )}>
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className={cn(
            "p-2 rounded-lg",
            isUrgent && "bg-red-100 dark:bg-red-900/20",
            isWarning && "bg-yellow-100 dark:bg-yellow-900/20",
            !isUrgent && !isWarning && "bg-blue-100 dark:bg-blue-900/20"
          )}>
            {isUrgent ? (
              <Clock className="h-5 w-5 text-red-600 dark:text-red-400" />
            ) : (
              <Sparkles className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            )}
          </div>
          
          <div className="flex-1">
            <p className={cn(
              "font-semibold",
              isUrgent && "text-red-900 dark:text-red-300",
              isWarning && "text-yellow-900 dark:text-yellow-300",
              !isUrgent && !isWarning && "text-blue-900 dark:text-blue-300"
            )}>
              {isUrgent 
                ? `Your trial expires in ${timeRemaining}!`
                : `Trial Active — ${timeRemaining} remaining`
              }
            </p>
            
            <p className={cn(
              "text-sm mt-1",
              isUrgent && "text-red-700 dark:text-red-400",
              isWarning && "text-yellow-700 dark:text-yellow-400",
              !isUrgent && !isWarning && "text-blue-700 dark:text-blue-400"
            )}>
              {isUrgent
                ? "Don't lose access to your premium features. Upgrade now!"
                : "Enjoying unlimited features? Keep them with an upgrade."
              }
            </p>
          </div>
        </div>
        
        <TrackingLink
          href="/pricing"
          campaign="trial_banner"
          content="full_upgrade_button"
          className={cn(
            "px-4 py-2 rounded-lg font-medium transition-colors whitespace-nowrap",
            isUrgent && "bg-red-600 text-white hover:bg-red-700 dark:bg-red-600 dark:hover:bg-red-700",
            isWarning && "bg-yellow-600 text-white hover:bg-yellow-700 dark:bg-yellow-600 dark:hover:bg-yellow-700",
            !isUrgent && !isWarning && "bg-blue-600 text-white hover:bg-blue-700 dark:bg-blue-600 dark:hover:bg-blue-700"
          )}
        >
          Upgrade Now
        </TrackingLink>
      </div>
    </div>
  );
}

/**
 * Global trial banner for main layout
 * Shows at the top of the app when user is in trial
 * Dismissible with session storage (resets on new login)
 */
export function GlobalTrialBanner() {
  const { subscription, isLoading } = useSubscriptionContext();
  const [isDismissed, setIsDismissed] = useState(false);
  const location = useLocation();
  
  // Check session storage for dismissal state
  useEffect(() => {
    const dismissed = sessionStorage.getItem('trial-banner-dismissed');
    setIsDismissed(dismissed === 'true');
  }, []);
  
  // Compute path-based hide flag so the CSS-variable side-effect below
  // correctly clears --trial-banner-height when the banner is suppressed
  // by route. Without this, NextGen routes would have a phantom 40px gap.
  const path = location.pathname;
  const hiddenByPath =
    ['/', '/terms', '/privacy', '/disclaimer', '/auth', '/auth/callback', '/resend-confirmation'].some(
      (p) => path === p || path.startsWith('/auth')
    )
    || path === '/nextgen-prep'
    || path.startsWith('/nextgen-prep/')
    || path.startsWith('/nextgen/');

  // Publish banner visibility to context so layout hooks can react.
  // MUST be before any conditional returns to maintain hooks order.
  const { setJdsVisible } = useTrialBannerContext();

  // Bar-package entitlement state. When the user holds an active/trial/purchased/scheduled
  // bar_package, JDS access is granted via the entitlement fallback in src/lib/subscription.ts
  // — the JDS trial banner becomes incorrect (subscription.status may still be 'trialing'
  // from a stale row) and must be suppressed. Hook must be called BEFORE the useEffect
  // below to maintain hooks order.
  const { entitlementState } = useBarPackageAccess();
  const suppressedByEntitlement =
    entitlementState === 'active' ||
    entitlementState === 'trial' ||
    entitlementState === 'purchased' ||
    entitlementState === 'scheduled';

  React.useEffect(() => {
    const visible =
      !isDismissed &&
      subscription?.status === 'trialing' &&
      !hiddenByPath &&
      !suppressedByEntitlement;
    setJdsVisible(visible);
    document.documentElement.style.setProperty(
      '--trial-banner-height',
      visible ? '40px' : '0px'
    );

    return () => {
      setJdsVisible(false);
      document.documentElement.style.setProperty('--trial-banner-height', '0px');
    };
  }, [isDismissed, subscription?.status, hiddenByPath, suppressedByEntitlement, setJdsVisible]);
  
  const handleDismiss = () => {
    sessionStorage.setItem('trial-banner-dismissed', 'true');
    setIsDismissed(true);
  };
  
  // hiddenByPath was already computed above for the CSS-variable side effect.
  // Recompute the human-readable variants for clarity in the early-return below.
  const isPublicPage =
    ['/', '/terms', '/privacy', '/disclaimer', '/auth', '/auth/callback', '/resend-confirmation'].some(
      (p) => path === p || path.startsWith('/auth')
    );
  // /nextgen-prep is the public NextGen marketing landing page; the JDS
  // GlobalTrialBanner ("upgrade to JDS Unlimited") is the wrong context there
  // since the page is selling NextGen ($499 one-time), not JDS Unlimited.
  // /nextgen/* is the in-product NextGen surface where NextGenTrialBanner is
  // already the authoritative entitlement banner — showing the JDS banner on
  // top would double-stack two banners with conflicting CTAs.
  const isNextGenSurface = path === '/nextgen-prep'
    || path.startsWith('/nextgen-prep/')
    || path.startsWith('/nextgen/');
  
  // Don't show if on public page, NextGen surface, loading, no subscription,
  // not trial, dismissed, or suppressed by an active bar_package entitlement.
  if (
    isPublicPage ||
    isNextGenSurface ||
    isLoading ||
    !subscription ||
    subscription.status !== 'trialing' ||
    isDismissed ||
    suppressedByEntitlement
  ) {
    return null;
  }
  
  const trialEndsAt = new Date(subscription.current_period_end || '');
  const now = new Date();
  
  // Don't show if trial has expired
  if (trialEndsAt <= now) {
    return null;
  }
  
  const daysRemaining = Math.ceil((trialEndsAt.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
  const isUrgent = daysRemaining <= 3;
  
  return (
    <div className={cn(
      // Inline block at top of app — pushes layout down naturally.
      // Must not be position:fixed; that caused it to overlap sticky navbars.
      "w-full flex-shrink-0",
      isUrgent
        ? "bg-gradient-to-r from-red-500 to-red-600"
        : "bg-gradient-to-r from-blue-500 to-blue-600"
    )}>
      <div 
        className="py-2 transition-all duration-300"
        style={{ 
          paddingLeft: '1rem',
          paddingRight: '1rem'
        }}
      >
        <div className="flex items-center justify-center relative">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              {isUrgent ? (
                <Clock className="h-4 w-4 text-white animate-pulse" />
              ) : (
                <Sparkles className="h-4 w-4 text-white" />
              )}
              <span className="text-sm font-medium text-white">
                {isUrgent 
                  ? `Trial expires in ${daysRemaining} day${daysRemaining === 1 ? '' : 's'}!`
                  : `Free trial: ${daysRemaining} day${daysRemaining === 1 ? '' : 's'} remaining`
                }
              </span>
            </div>
            
            <TrackingLink
              href="/pricing"
              campaign="trial_banner"
              content="global_upgrade_link"
              className="text-sm font-semibold text-white underline hover:no-underline"
            >
              Upgrade now →
            </TrackingLink>
          </div>
          
          <button
            onClick={handleDismiss}
            className="absolute right-0 p-1 hover:bg-white/20 rounded transition-colors"
            aria-label="Dismiss trial banner"
          >
            <X className="h-4 w-4 text-white" />
          </button>
        </div>
      </div>
    </div>
  );
}